import React, { useContext, useState } from "react";
import { Box } from "Components/Base/Layout/Box";
import { LearningActivityCard } from "Components/Module/LearningActivityItem";
import ActivityDescription from "Components/Module/LearningActivityItem/Description";
import {
  useRating,
  getCardTitle,
  formatCourseDuration,
  renderTimeDisplay,
  getCardImage,
} from "Components/Module/LearningActivityItem/functions";
import Rating from "lib/components/Rating";
import { AddLinebreaksRemoveHtmlTags } from "lib/utils";
import { sanitize } from "lib/sanitizer.js";
import {
  useCourseCompletion,
  getDisplayDescription,
} from "../../../../functions";
import LangContext from "../../../../langContext";
import ProgramCourseDetails from "../ProgramCourseDetails";
import ProgramContext from "../../ProgramContext";
import { ClassroomProgramChips } from "./CardChips";
import { ClassroomProgramActions } from "./CardActions";
import ProgramTypeClassroomDates from "./ProgramTypeClassroomDates";
import CourseReason from "./CourseReason";
import { OBJECT_TYPE_CLASSROOM } from "../../../../constants";

const ClassroomProgramCard = (props) => {
  const [displayCourseDetails, setDisplayCourseDetails] = useState(false);
  const ProgramSettings = useContext(ProgramContext);
  const languageContext = useContext(LangContext);

  const { course, displayImage, activityId, isPortalOverview, isPreview } =
    props;

  const [completed, setCompleted] = useCourseCompletion(course);
  const { rating, comment, updateRating } = useRating(course);
  const { description, shouldDisplay } = getDisplayDescription(
    course,
    isPortalOverview,
  );

  const displayCourseDates = false;
  const courseDates = {
    startDate: course.courseStarts,
    endDate: course.courseEnds,
  };

  const displayTime = formatCourseDuration(course, languageContext);

  const displayRating =
    course.allowRating && !course.requested && !isPortalOverview;

  const { imageComponent } = getCardImage(
    course,
    displayImage,
    isPortalOverview,
  );

  const cardTitle = getCardTitle(course, isPortalOverview);

  const ClassroomCardContent = () => {
    return (
      <>
        {isPortalOverview && (
          <Rating
            currentRating={course.avgRating}
            noOfRatings={course.noOfRatings}
            readOnly={true}
          />
        )}

        {displayRating && (
          <Rating
            currentRating={rating}
            currentComment={comment}
            type={OBJECT_TYPE_CLASSROOM}
            objectId={course.courseObjectId}
            updateRating={updateRating}
            readOnly={false}
            managerView={ProgramSettings.managerView}
          />
        )}

        {displayCourseDates && (
          <ProgramTypeClassroomDates dates={courseDates} />
        )}

        {shouldDisplay && (
          <ActivityDescription
            description={AddLinebreaksRemoveHtmlTags(sanitize(description))}
          />
        )}

        {isPortalOverview && (
          <CourseReason
            reason={course.reason}
            label={`${languageContext("reason")}: \n`}
          />
        )}

        <Box
          display="flex"
          justifyContent={displayTime ? "space-between" : "center"}
          width="100%"
        >
          {renderTimeDisplay(displayTime)}

          {displayCourseDetails && !isPortalOverview && (
            <ProgramCourseDetails
              course={course}
              onClose={setDisplayCourseDetails}
              activityId={activityId}
            />
          )}
        </Box>
      </>
    );
  };

  return (
    <LearningActivityCard
      id={`learningActivityCard${course.courseId}`}
      title={cardTitle}
      type={course.type}
      displayImage={displayImage}
      imageComponent={imageComponent}
      chips={
        <ClassroomProgramChips
          course={course}
          completed={completed}
          languageContext={languageContext}
          isPortalOverview={isPortalOverview}
        />
      }
      content={<ClassroomCardContent />}
      actions={
        <ClassroomProgramActions
          course={course}
          isPreview={isPreview}
          languageContext={languageContext}
          isPortalOverview={isPortalOverview}
          ProgramSettings={ProgramSettings}
          activityId={activityId}
          onClick={() => setDisplayCourseDetails(true)}
        />
      }
    />
  );
};

export default ClassroomProgramCard;
